// Headset VR (WebXR immersive-vr): the visitor stands in the flat at real scale. The camera hangs under a rig
// entity; the headset moves the camera inside the rig, the controllers move the rig:
//  * left stick walks where the head looks, with collision, like walking on screen;
//  * right stick turns in steps (smooth turning makes many people queasy);
//  * trigger (or a pinch on hand-tracking headsets) jumps to the floor point the ray shows (walkable floor the
//    visitor could also walk to, not a table top or a closed-off room).
// Walking around the real room moves the body too; walls stop it (the view stays at the wall).
import { Color, Vec3, XRSPACE_LOCAL, XRSPACE_LOCALFLOOR, XRTYPE_VR } from 'playcanvas';

const DEG = Math.PI / 180;
const SNAP = 30;            // deg per turn step
const DEAD = 0.15;          // stick dead zone
const RAY_REACH = 30;       // m
const RAY_OK = new Color(1, 1, 1);
const RAY_NO = new Color(0.85, 0.35, 0.3);
const damp = (rate, dt) => 1 - Math.exp(-rate * dt);
const dead = v => (Math.abs(v) < DEAD ? 0 : (v - Math.sign(v) * DEAD) / (1 - DEAD));
const head = new Vec3(), from = new Vec3(), to = new Vec3();
const ring = Array.from({ length: 48 }, () => new Vec3());

export class VrMode {
    /**
     * @param {import('playcanvas').AppBase} app - created with an XrManager
     * @param {import('playcanvas').Entity} rig - the camera's parent, identity outside VR
     * @param {import('playcanvas').Entity} camera
     * @param {import('./collision/character.js').Character} character
     * @param {object} walk - manifest.walk
     * @param {() => import('./collision/navgrid.js').NavGrid} nav - the navigation grid (built on first call)
     */
    constructor(app, rig, camera, character, walk, nav) {
        this.app = app;
        this.xr = app.xr;
        this.rig = rig;
        this.camera = camera;
        this.ch = character;
        this.walk = walk;
        this.nav = nav;
        this.reach = null;          // nav nodes reachable from where the visitor stands
        this.yaw = 0;               // rig heading (deg)
        this.floor = 0;             // rig height above the feet: 0 in local-floor space, eye height in local space
        this.y = null;              // smoothed rig height (steps, sills)
        this.vel = [0, 0];
        this.turnArmed = true;
        this.onStart = null;        // () => void
        this.onEnd = null;          // (yaw) => void: where the head looked, to continue on screen
        if (!this.xr) return;
        this.xr.on('start', () => { if (this.active) this._begin(); });
        this.xr.on('end', () => this._finish());
        this.xr.input.on('select', source => this._jump(source));
    }

    get active() {
        return !!this.xr?.active && this.xr.type === XRTYPE_VR;
    }

    /** Calls `cb(available)` now and whenever a headset appears or goes. */
    watch(cb) {
        if (!this.xr) return cb(false);
        this.xr.on('available', (type, ok) => { if (type === XRTYPE_VR) cb(ok); });
        cb(this.xr.isAvailable(XRTYPE_VR));
    }

    /** Enter VR facing `yaw`; resolves false if the headset refused. Needs a user gesture (a button press). */
    start(yaw) {
        this.yaw = yaw;
        return new Promise((resolve) => {
            // Floor-level tracking puts the visitor's real eye height into the flat; headsets without it start at
            // the tour's eye height
            const go = (space, fallback) => this.camera.camera.startXr(XRTYPE_VR, space, {
                callback: (err) => {
                    if (err && fallback) return go(fallback, null);
                    if (!err) this.floor = space === XRSPACE_LOCAL ? this.walk.eyeHeight : 0;
                    resolve(!err);
                }
            });
            go(XRSPACE_LOCALFLOOR, XRSPACE_LOCAL);
        });
    }

    _begin() {
        const [x, y, z] = this.ch.pos;
        this.rig.setPosition(x, y + this.floor, z);
        this.rig.setEulerAngles(0, this.yaw, 0);
        this.y = null;
        this.vel = [0, 0];
        this.onStart?.();
    }

    _finish() {
        const f = this.camera.forward;
        const yaw = Math.atan2(-f.x, -f.z) / DEG;
        this.rig.setPosition(0, 0, 0);
        this.rig.setEulerAngles(0, 0, 0);
        this.onEnd?.(yaw);
    }

    update(dt) {
        let mx = 0, my = 0, turn = 0;
        for (const src of this.xr.input.inputSources) {
            const ax = src.gamepad?.axes;
            if (!ax) continue;
            // xr-standard mapping: the thumbstick on axes 2/3 (0/1 is a touchpad, or the stick on older runtimes)
            const sx = dead(ax.length >= 4 ? ax[2] : ax[0] ?? 0), sy = dead(ax.length >= 4 ? ax[3] : ax[1] ?? 0);
            if (src.handedness === 'right') turn = sx;
            else { mx = sx; my = -sy; }
        }
        head.copy(this.camera.getPosition());
        if (this.turnArmed && Math.abs(turn) > 0.7) {
            this._turn(-Math.sign(turn) * SNAP);
            this.turnArmed = false;
        } else if (Math.abs(turn) < 0.3) {
            this.turnArmed = true;
        }

        // Stick: along the floor where the head looks
        const f = this.camera.forward;
        const fl = Math.hypot(f.x, f.z) || 1, fx = f.x / fl, fz = f.z / fl;
        const speed = this.walk.speed;
        const k = damp(10, dt);
        this.vel[0] += ((fx * my - fz * mx) * speed - this.vel[0]) * k;
        this.vel[1] += ((fz * my + fx * mx) * speed - this.vel[1]) * k;

        // The body goes where the head is plus the stick's way; what the walls hold back shifts the rig, so the
        // head stays above the body
        const ch = this.ch;
        const dx = head.x - ch.pos[0] + this.vel[0] * dt, dz = head.z - ch.pos[2] + this.vel[1] * dt;
        if (Math.abs(dx) > 1e-4 || Math.abs(dz) > 1e-4 || !ch.onGround) ch.move(dx, dz, dt);
        const p = this.rig.getPosition();
        const ty = ch.pos[1] + this.floor;
        this.y = this.y === null ? ty : this.y + (ty - this.y) * damp(12, dt);
        this.rig.setPosition(p.x + ch.pos[0] - head.x, this.y, p.z + ch.pos[2] - head.z);

        this._drawRays();
    }

    /** Turn the rig about the head, so the visitor turns on the spot. */
    _turn(deg) {
        const p = this.rig.getPosition(), r = deg * DEG, c = Math.cos(r), s = Math.sin(r);
        const ox = p.x - head.x, oz = p.z - head.z;
        this.rig.setPosition(head.x + ox * c + oz * s, p.y, head.z - ox * s + oz * c);
        this.yaw += deg;
        this.rig.setEulerAngles(0, this.yaw, 0);
    }

    /** What a controller's ray hits: {point, floor} (floor: walkable and reachable from here) or null. */
    _target(src) {
        const o = src.getOrigin(), d = src.getDirection();
        const hit = this.ch.grid.raycast([o.x, o.y, o.z], [d.x, d.y, d.z], RAY_REACH);
        if (!hit) return null;
        let floor = hit.normal[1] === 1;
        if (floor) {
            // An upward face is a floor only where the navigation grid has walkable ground at that height
            const nav = this.nav(), [x, y, z] = hit.point;
            const id = nav.nearest(x, y, z, 0.3);
            floor = id >= 0 && Math.abs(nav.nodes[id].y - y) < 0.15 && !!this._reachable(nav)[id];
        }
        return { point: hit.point, floor };
    }

    /** Nodes the visitor could walk to from here; computed again only after reaching another, separate area. */
    _reachable(nav) {
        const here = nav.nearest(this.ch.pos[0], this.ch.pos[1], this.ch.pos[2], 1.0);
        if (!this.reach?.[here]) this.reach = nav.reachable(here);
        return this.reach;
    }

    _jump(src) {
        if (!this.active) return;
        const t = this._target(src);
        if (!t?.floor) return;
        head.copy(this.camera.getPosition());
        if (!this.ch.teleport(t.point[0], t.point[1] + 0.3, t.point[2])) return;
        const p = this.rig.getPosition();
        this.rig.setPosition(p.x + this.ch.pos[0] - head.x, p.y, p.z + this.ch.pos[2] - head.z);
        this.y = null;
        this.vel = [0, 0];
    }

    /** Controller rays: white with a ring where a jump would land, red where it cannot (wall, furniture). */
    _drawRays() {
        for (const src of this.xr.input.inputSources) {
            if (src.targetRayMode !== 'tracked-pointer') continue;
            const o = src.getOrigin(), d = src.getDirection();
            const t = this._target(src);
            from.copy(o);
            if (t) to.set(...t.point); else to.copy(d).mulScalar(3).add(o);
            this.app.drawLine(from, to, t?.floor ? RAY_OK : RAY_NO);
            if (!t?.floor) continue;
            for (let i = 0; i < 24; i++) {
                for (const [j, a] of [[2 * i, i], [2 * i + 1, i + 1]]) {
                    const r = (a / 24) * Math.PI * 2;
                    ring[j].set(t.point[0] + Math.cos(r) * 0.2, t.point[1] + 0.02, t.point[2] + Math.sin(r) * 0.2);
                }
            }
            this.app.drawLines(ring, RAY_OK);
        }
    }
}
